import React from "react";
import { Box } from "@mui/material";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { Rating, StatusChip, chipBase, statusOf } from "../RestaurantList/RestaurantListCard";
import { resizedImage, resizedSrcSet } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS } from "../../utils/designTokens";
import { cuisineLine, milesText, readyMinutesOf } from "./pickupText";

export const pickupButtonSx = {
  ...BUTTON_RESET,
  ...FOCUS_RING,
  width: "100%",
  minHeight: 44,
  px: 2,
  borderRadius: RADIUS.pill,
  bgcolor: C.red,
  color: "#FFFFFF",
  fontFamily: FONT_BODY,
  fontSize: 15,
  fontWeight: 700,
  textAlign: "center",
  transition: "background-color .15s ease",
  "&:hover": { bgcolor: C.redHover },
};

/** The dark "Pickup only" chip, so nobody reads the card as delivering to them. */
export function PickupOnlyChip() {
  return (
    <Box component="span" sx={{ ...chipBase, bgcolor: C.ink, color: "#FFFFFF" }}>
      <ShoppingBagOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
      Pickup only
    </Box>
  );
}

function Info({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
      <Icon aria-hidden="true" sx={{ fontSize: 16, color: C.muted }} />
      {children}
    </Box>
  );
}

/** "6.7 mi · 23 min drive · Ready in ~15 min". */
export function PickupFacts({ r, driveMinutes }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.75, rowGap: 0.5, fontFamily: FONT_BODY, fontSize: 13, color: C.muted }}>
      <Info icon={PlaceOutlinedIcon}>{milesText(r.distance)}</Info>
      <Info icon={DirectionsCarOutlinedIcon}>{driveMinutes ? `${driveMinutes} min drive` : null}</Info>
      <Info icon={AccessTimeRoundedIcon}>{`Ready in ~${readyMinutesOf(r)} min`}</Info>
    </Box>
  );
}

/**
 * One restaurant on the no-delivery view. The name is the card's link
 * (stretched over it); "Order for pickup" does the same, above the link.
 */
function PickupCard({ r, driveMinutes, onOrder }) {
  const status = statusOf(r);
  const closed = status.kind !== "open";
  const image = r.image || r.logo;
  const logo = r.image && r.logo && r.logo !== r.image ? r.logo : null;
  const line = cuisineLine(r);

  return (
    <Box
      component="article"
      sx={{
        position: "relative",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        "&:hover .pk-img": { transform: "scale(1.03)" },
        "&:focus-within .pk-name": { textDecoration: "underline" },
      }}
    >
      <Box sx={{ position: "relative", aspectRatio: "16 / 10", borderRadius: "18px", overflow: "hidden", bgcolor: C.surface2 }}>
        {image && (
          <Box
            component="img"
            className="pk-img"
            src={resizedImage(image, 800)}
            srcSet={resizedSrcSet(image, [480, 800])}
            sizes="(max-width: 600px) 100vw, 360px"
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: closed ? "grayscale(35%) brightness(0.92)" : "none",
              transition: "transform .3s ease",
            }}
          />
        )}
        <Box sx={{ position: "absolute", top: 12, left: 12, right: 12, display: "flex", gap: 0.75, flexWrap: "wrap", pointerEvents: "none" }}>
          <PickupOnlyChip />
          <StatusChip status={status} />
        </Box>
        {logo && (
          <Box
            component="img"
            src={resizedImage(logo, 128)}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
              position: "absolute",
              right: 12,
              bottom: 12,
              width: 44,
              height: 44,
              boxSizing: "border-box",
              borderRadius: "12px",
              border: "2px solid #FFFFFF",
              bgcolor: C.bg,
              objectFit: "cover",
              boxShadow: "0 2px 8px rgba(26,25,24,0.20)",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>

      <Box sx={{ pt: 1.5, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="button"
            type="button"
            className="pk-name"
            onClick={() => onOrder(r)}
            aria-label={`${r.name}, pickup only`}
            sx={{
              ...BUTTON_RESET,
              flex: 1,
              minWidth: 0,
              textAlign: "left",
              fontFamily: FONT_BODY,
              fontSize: 17,
              fontWeight: 700,
              lineHeight: 1.3,
              color: C.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "18px" },
              "&:focus-visible": { outline: "none" },
              "&:focus-visible::after": { outline: `2px solid ${C.ink}`, outlineOffset: "4px" },
            }}
          >
            {r.name}
          </Box>
          <Rating r={r} />
        </Box>
        {line && (
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, color: C.muted, mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {line}
          </Box>
        )}
        <Box sx={{ mt: 0.75 }}>
          <PickupFacts r={r} driveMinutes={driveMinutes} />
        </Box>
      </Box>

      <Box
        component="button"
        type="button"
        onClick={() => onOrder(r)}
        aria-label={`Order for pickup from ${r.name}`}
        sx={{ ...pickupButtonSx, position: "relative", zIndex: 1, mt: 1.5 }}
      >
        Order for pickup
      </Box>
    </Box>
  );
}

export default React.memo(PickupCard);
